import { Helmet } from 'react-helmet-async';
import Calculator from '../components/Calculator';
import RelatedCalculators from '../components/RelatedCalculators';
import { detectLocaleSync } from '../locales/config';

const COLORS = { bg: "#0a0a0f", text: "#e8e8f0", muted: "#9999b5", accent: "#00e5a0", border: "#1e1e2e" };

const faqItems = [
  {
    q: 'What is the debt snowball method?',
    a: 'Pay minimums on all debts, then put all extra money toward the smallest balance first. Once it\'s paid off, roll that payment into the next smallest. Quick wins keep you motivated.',
  },
  {
    q: 'Snowball vs Avalanche — which is better?',
    a: 'Avalanche saves the most money (targets highest interest first). Snowball builds momentum with quick wins. If staying motivated is your challenge, snowball often leads to better real-world results.',
  },
  {
    q: 'How fast can I pay off debt with the snowball method?',
    a: 'It depends on your balances and interest rates, but most people see their first debt eliminated within 6–18 months. Each payoff accelerates the next one.',
  },
  {
    q: 'Should I stop using credit cards while snowballing debt?',
    a: "Yes, ideally. New charges on paid-off cards would restart the cycle. Consider freezing cards or switching to a debit card while executing your payoff plan.",
  },
];

export default function SnowballCalculator() {
  const locale = detectLocaleSync();
  return (
    <div style={{ background: COLORS.bg, minHeight: '100vh', fontFamily: "'DM Sans', sans-serif", color: COLORS.text }}>
      <Helmet>
        <title>Free Debt Snowball Calculator — ClearMyDebt</title>
        <meta name="description" content="Use the debt snowball method to pay off your smallest debts first and build momentum. Free calculator — see your debt-free date instantly." />
        <link rel="canonical" href="https://clearmydebt.app/debt-snowball-calculator/" />
        <meta property="og:title" content="Free Debt Snowball Calculator — ClearMyDebt" />
        <meta property="og:description" content="Use the debt snowball method — pay smallest balances first for quick wins. Free, instant." />
        <meta property="og:url" content="https://clearmydebt.app/debt-snowball-calculator/" />
        <meta property="og:image" content="https://clearmydebt.app/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://clearmydebt.app/og-image.png" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqItems.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a },
          })),
        })}</script>
      </Helmet>

      <div style={{ maxWidth: 680, margin: '0 auto', padding: '48px 24px 0' }}>
        <div style={{ fontSize: '11px', letterSpacing: '3px', color: COLORS.accent, fontWeight: 700, marginBottom: '12px' }}>
          <a href="/" style={{ color: COLORS.accent, textDecoration: 'none' }}>ClearMyDebt</a>
        </div>
        <h1 style={{ fontSize: 'clamp(24px,5vw,36px)', fontWeight: 900, letterSpacing: '-1px', margin: '0 0 12px', background: `linear-gradient(135deg,#fff,${COLORS.accent})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          Free Debt Snowball Calculator
        </h1>
        <p style={{ color: COLORS.muted, fontSize: '1.05rem', marginBottom: 0, lineHeight: 1.6 }}>
          Pay off your smallest debts first and build unstoppable momentum. Enter your debts
          below and see exactly when you'll be debt-free using the snowball method.
        </p>
      </div>

      <Calculator
        defaultMethod="snowball"
        locale={locale}
        showHelmet={false}
        showHeader={false}
      />

      <div style={{ maxWidth: 680, margin: '0 auto', padding: '0 24px 64px' }}>
        <h2 style={{ color: COLORS.text, fontSize: '1.3rem', fontWeight: 700, margin: '0 0 12px' }}>
          How the Debt Snowball method works
        </h2>
        <p style={{ color: COLORS.muted, lineHeight: 1.7, marginBottom: 28 }}>
          List all your debts from smallest to largest balance. Pay minimums on everything,
          then throw every extra dollar at the smallest debt. Once it's gone, roll that
          payment into the next one — your "snowball" grows larger with every payoff.
        </p>

        <h2 style={{ color: COLORS.text, fontSize: '1.3rem', fontWeight: 700, margin: '0 0 12px' }}>
          Why the Snowball works psychologically
        </h2>
        <p style={{ color: COLORS.muted, lineHeight: 1.7, marginBottom: 28 }}>
          Paying off a whole debt — even a small one — gives a real dopamine hit. Research
          shows that behaviour change is more about motivation than math. If you've tried
          avalanche and lost steam, snowball might be the method that actually gets you there.
        </p>

        <h2 style={{ color: COLORS.text, fontSize: '1.3rem', fontWeight: 700, margin: '0 0 20px' }}>
          Frequently Asked Questions
        </h2>
        {faqItems.map(({ q, a }) => (
          <div key={q} style={{ marginBottom: 20, padding: '16px', background: '#13131a', borderRadius: 10, border: `1px solid ${COLORS.border}` }}>
            <h3 style={{ color: COLORS.text, fontSize: '1rem', fontWeight: 600, margin: '0 0 8px' }}>{q}</h3>
            <p style={{ color: COLORS.muted, lineHeight: 1.6, margin: 0 }}>{a}</p>
          </div>
        ))}

      </div>

      <RelatedCalculators locale={locale} exclude="/debt-snowball-calculator/" />
    </div>
  );
}
